import { languageNameToCode } from 'util/languages'
import { PlanId, stripePriceIds } from 'util/prices'
import {
  allowedAudioExtensions,
  allowedVideoExtensions,
} from 'util/validateFileExtension'

const getAnnualPrice = (price: string) => {
  // price comes in as '$199' and we need to convert it to 199 * 12:
  return Number(price.replace(/\$/g, '')) * 12
}

export interface Plan {
  name: string
  id: PlanId
  description: string
  price: string
  originalPrice?: string
  billingCycle: 'Monthly' | 'Annual'
  minutes?: number
  condensedDescription: string
}

export interface Feature {
  name: string
  values: { [planName: string]: string | boolean }
  tooltip?: string
}

export interface FeatureGroup {
  groupName: string
  features: Feature[]
}

export const PLANS: Plan[] = [
  {
    name: 'Base',
    id: 'base',
    description: 'Get started with all features',
    price: '$19',
    originalPrice: '$39',
    billingCycle: 'Monthly',
    minutes: 300,
    condensedDescription: '300 upload minutes / 1 included team seat.',
  },
  {
    name: 'Plus',
    id: 'plus',
    description: 'Best for consistent content',
    price: '$29',
    originalPrice: '$59',
    billingCycle: 'Monthly',
    minutes: 800,
    condensedDescription: '800 upload minutes / 1 included team seat.',
  },
  {
    name: 'Max',
    id: 'max',
    description: 'Best for teams or large projects',
    price: '$149',
    originalPrice: '$299',
    billingCycle: 'Monthly',
    minutes: 10000,
    condensedDescription: '10,000 upload minutes / 5 included team seats.',
  },
  {
    name: 'Base',
    id: 'baseYearly',
    description: 'Start with all our features',
    price: '$9',
    originalPrice: '$19',
    billingCycle: 'Annual',
    minutes: 300,
    condensedDescription: '300 upload minutes / 1 included team seat.',
  },
  {
    name: 'Plus',
    id: 'plusYearly',
    description: 'Create more with more minutes',
    price: '$19',
    originalPrice: '$39',
    billingCycle: 'Annual',
    minutes: 800,
    condensedDescription: '800 upload minutes / 1 included team seat.',
  },
  {
    name: 'Max',
    id: 'maxYearly',
    description: 'Best for teams or large projects',
    price: '$99',
    originalPrice: '$199',
    billingCycle: 'Annual',
    minutes: 10000,
    condensedDescription: '10,000 upload minutes / 5 included team seats.',
  },
]

export const FEATURE_GROUPS: FeatureGroup[] = [
  {
    groupName: '',
    features: [
      {
        name: 'Monthly Plan Minutes',
        tooltip:
          'The total number of minutes you can upload in a month. This is the total number of minutes for all users in your team.',
        values: {
          base: '300',
          baseYearly: '300',
          plus: '800',
          plusYearly: '800',
          max: '10,000',
          maxYearly: '10,000',
        },
      },
      {
        name: 'Included team seats',
        tooltip:
          "You will need 1 seat for each user on your plan (you are included in the seat count). Team members share access to the team's features, Plan Minutes, and On-Demand Minutes. You can assign teammates to specific Projects, giving them access only to what's relevant.",
        values: {
          base: '1',
          baseYearly: '1',
          plus: '1',
          plusYearly: '1',
          max: '5',
          maxYearly: '5',
        },
      },
      {
        name: 'Additional seats',
        values: {
          base: '$19',
          baseYearly: '$11',
          plus: '$19',
          plusYearly: '$11',
          max: '$19',
          maxYearly: '$11',
        },
      },
      {
        name: 'Projects',
        values: {
          base: '1',
          baseYearly: '1',
          plus: '5',
          plusYearly: '5',
          max: 'Unlimited',
          maxYearly: 'Unlimited',
        },
      },
      {
        name: 'Create Chatbots',
        tooltip: 'Create and customize AI chatbots based on your content.',
        values: {
          base: '3',
          baseYearly: '3',
          plus: '6',
          plusYearly: '6',
          max: 'Unlimited',
          maxYearly: 'Unlimited',
        },
      },
    ],
  },

  {
    groupName: 'AI Content Creation',
    features: [
      {
        name: 'Prompt Templates',
        values: {
          base: '150+',
          baseYearly: '150+',
          plus: '150+',
          plusYearly: '150+',
          max: '150+',
          maxYearly: '150+',
        },
      },
      {
        name: 'Prompt Packs',
        tooltip:
          'Pre-designed or customizable sets of AI prompts that process your uploaded content in one go. Choose from 10 ready-made packs or create your own to streamline a specific workflow.',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Custom Prompts',
        tooltip:
          'Write and save your own prompts. Run them on your uploaded content.',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Outcast Writing Studio',
        tooltip:
          'A new way to write with AI-powered features. Write, edit, and format your content with ease.',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Autocomplete',
        tooltip:
          'Type ++ anywhere in your generated content to have Outcast continue writing for you.',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
    ],
  },
  {
    groupName: 'Clip Creation',
    features: [
      {
        name: 'Captioned Clips',
        tooltip: 'Cut and caption your clips with AI-powered features.',
        values: {
          base: 'Unlimited',
          baseYearly: 'Unlimited',
          plus: 'Unlimited',
          plusYearly: 'Unlimited',
          max: 'Unlimited',
          maxYearly: 'Unlimited',
        },
      },
      {
        name: 'Audiograms',
        tooltip:
          'Automatically create audiograms from your uploaded content. Customize details like images, background color, font, and more.',
        values: {
          base: 'Unlimited',
          baseYearly: 'Unlimited',
          plus: 'Unlimited',
          plusYearly: 'Unlimited',
          max: 'Unlimited',
          maxYearly: 'Unlimited',
        },
      },

      {
        name: 'Viral Clip Ideas',
        tooltip:
          'Outcast processes your uploaded content and pulls clip ideas for you so you can easily organize your content.',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Transcript Cut',
        tooltip:
          'Cut your content into clips by selecting the transcript segments you want to use.',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Max Clip Export',
        values: {
          base: '15 minutes',
          baseYearly: '15 minutes',
          plus: '15 minutes',
          plusYearly: '15 minutes',
          max: '15 minutes',
          maxYearly: '15 minutes',
        },
      },

      {
        name: 'Social Specific Export',
        tooltip:
          'Export your clips to formats specific to social media platforms like TikTok, Instagram, and YouTube. Outcast supports exporting to 16x9, 9x16, and 1x1 aspect ratios.',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
    ],
  },
  {
    groupName: 'Transcription',
    features: [
      {
        name: 'Best-in-Class Accuracy',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Supported Languages',
        tooltip: `Outcast can transcribe and create content in ${Object.keys(
          languageNameToCode
        )
          .filter(
            (lang) => !lang.includes('British') && !lang.includes('Australian')
          )
          .map((lang) => (lang.includes('US') ? 'English' : lang))
          .map((lang, index, arr) =>
            index === arr.length - 1 ? `and ${lang}` : lang
          )
          .join(', ')}`,
        values: {
          base: '17',
          baseYearly: '17',
          plus: '17',
          plusYearly: '17',
          max: '17',
          maxYearly: '17',
        },
      },
      {
        name: 'Transcript Editor',
        tooltip:
          'Change words, phrases, and sentences without worrying about messing up the timings of words or transcript formatting. It just works.',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Formatted Export',
        tooltip:
          'Export your transcript in the exact format you need. Include speaker names, timestamps, and more.',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
    ],
  },
  {
    groupName: 'Chatbots',
    features: [
      {
        name: 'Embeddable',
        tooltip: 'Embed your chatbots on your own website or app.',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Multi Upload Training',
        tooltip:
          'Chatbots can be trained on multiple or all of the uploads in a project.',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Custom Persona',
        tooltip:
          'Give your chatbot an identity that matches your brand by tweaking the underlying prompts.',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Branding',
        tooltip: 'Add your logo and brand colors to your chatbot.',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Referral Program',
        tooltip:
          'For every new customer automatically referred through your chatbot, earn 30% of the subscription fee for the first year.',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
    ],
  },
  {
    groupName: 'Prompt Templates',
    features: [
      {
        name: 'LinkedIn and X Posts',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Newsletters',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Important Timestamps',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Quotable Moments',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Quizzes',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Custom Prompt Packs',
        tooltip:
          'Organize your prompts into packs for easy reuse across uploads. Create your own packs or use our pre-made templates.',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Export to PDF',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
    ],
  },
  {
    groupName: 'Support',
    features: [
      {
        name: 'Founder Email Support',
        tooltip:
          'We are a small team, but we are always here to help. Email us anytime and we will get back to you ASAP with a custom solution.',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },

      {
        name: 'Feature Request',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
    ],
  },
  {
    groupName: 'Import Methods',
    features: [
      {
        name: 'Audio files',
        tooltip:
          'Supported formats: ' +
          allowedAudioExtensions
            .map((ext) => ext.replace(/\./g, ''))
            .join(', '),
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Video files',
        tooltip:
          'Supported formats: ' +
          // map each extension to its value without periods
          allowedVideoExtensions
            .map((ext) => ext.replace(/\./g, ''))
            .join(', '),
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'YouTube URL',
        tooltip: 'Import videos from YouTube by pasting the URL.',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Reddit post',
        tooltip: 'Import Reddit posts by pasting the URL.',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'TikTok video',
        tooltip: 'Import TikTok videos by pasting the URL.',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Instagram reel',
        tooltip: 'Import Instagram reels by pasting the URL.',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Facebook post',
        tooltip: 'Import Facebook posts by pasting the URL.',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Twitter/X post',
        tooltip: 'Import Twitter/X posts by pasting the URL.',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'RSS Feed import',
        values: {
          base: true,
          baseYearly: true,
          plus: true,
          plusYearly: true,
          max: true,
          maxYearly: true,
        },
      },
      {
        name: 'Concurrent Uploads',
        tooltip:
          'Upload multiple files at once. You can upload up to 5 files at a time.',
        values: {
          base: '5',
          baseYearly: '5',
          plus: '5',
          plusYearly: '5',
          max: '5',
          maxYearly: '5',
        },
      },
    ],
  },
]
export const PriceIdToPriceValue: Record<string, string | undefined> = {
  [stripePriceIds.base as string]: PLANS.find((plan) => plan.id === 'base')
    ?.price,
  [stripePriceIds.baseYearly as string]: `$${getAnnualPrice(
    PLANS.find((plan) => plan.id === 'baseYearly')?.price ?? ''
  )}`,
  [stripePriceIds.plus as string]: PLANS.find((plan) => plan.id === 'plus')
    ?.price,
  [stripePriceIds.plusYearly as string]: `$${getAnnualPrice(
    PLANS.find((plan) => plan.id === 'plusYearly')?.price ?? ''
  )}`,
  [stripePriceIds.max as string]: PLANS.find((plan) => plan.id === 'max')
    ?.price,
  [stripePriceIds.maxYearly as string]: `$${getAnnualPrice(
    PLANS.find((plan) => plan.id === 'maxYearly')?.price ?? ''
  )}`,
}
